import type { Ref } from 'vue';

import { computed, ref, watch } from 'vue';

import type { CartsTransactionsApi } from '@/services/booking/cartsTransactionsApi';
import type {
  CartDetailsItemResponse,
  GuestCustomerResponse,
  RequiredCustomerField,
  SemanticType,
} from '@/types/carts-transactions-api';

import { isValidDate, isValidEmail, isValidPhone } from './useContactFieldsForm';

// Semantic types that have dedicated inputs in the customer form (firstname, name, email, phone).
// Other types (POSTAL_CODE, COUNTRY, OTHER) are rendered as dynamic additional fields.
const CUSTOMER_FORM_SEMANTIC_TYPES: ReadonlySet<SemanticType> = new Set([
  'FIRST_NAME',
  'LAST_NAME',
  'EMAIL_ADDRESS',
  'PHONE_NUMBER',
]);

export function useGuestLogin(
  cartsTransactionsApi: CartsTransactionsApi,
  requiredCustomerFields: Ref<RequiredCustomerField[]>,
  cartItems: Ref<CartDetailsItemResponse[]>,
) {
  const isModalOpen = ref(false);
  const isSubmitting = ref(false);
  const isLoadingCustomer = ref(false);
  const submitError = ref<string | null>(null);
  const guestCustomer = ref<GuestCustomerResponse | null>(null);

  const firstname = ref('');
  const name = ref('');
  const email = ref('');
  const confirmEmail = ref('');
  const phone = ref('');
  const additionalFieldValues = ref<Record<string, string>>({});

  const additionalFields = computed(() => {
    return requiredCustomerFields.value.filter(
      field => !CUSTOMER_FORM_SEMANTIC_TYPES.has(field.semanticType),
    );
  });

  const phoneRequired = computed(() => {
    return requiredCustomerFields.value.some(
      field => field.semanticType === 'PHONE_NUMBER' && field.required,
    );
  });

  const emailsMatch = computed(() => {
    return email.value === confirmEmail.value;
  });

  const emailFormatError = computed(() => {
    return email.value.trim() !== '' && !isValidEmail(email.value.trim());
  });

  const phoneFormatError = computed(() => {
    return phone.value.trim() !== '' && !isValidPhone(phone.value);
  });

  const isFormValid = computed(() => {
    const baseValid = firstname.value.trim() !== ''
      && name.value.trim() !== ''
      && email.value.trim() !== ''
      && confirmEmail.value.trim() !== ''
      && emailsMatch.value
      && isValidEmail(email.value.trim());

    if (!baseValid)
      return false;
    if (phoneRequired.value && phone.value.trim() === '')
      return false;
    if (phoneFormatError.value)
      return false;

    for (const field of additionalFields.value) {
      if (!field.required)
        continue;
      const value = (additionalFieldValues.value[field.name] ?? '').trim();
      if (!value)
        return false;
      if (field.type === 'EMAIL' && !isValidEmail(value))
        return false;
      if (field.type === 'DATE' && !isValidDate(value))
        return false;
    }

    return true;
  });

  function getValueForSemanticType(field: RequiredCustomerField): string {
    switch (field.semanticType) {
      case 'FIRST_NAME': return firstname.value.trim();
      case 'LAST_NAME': return name.value.trim();
      case 'EMAIL_ADDRESS': return email.value.trim();
      case 'PHONE_NUMBER': return phone.value.trim();
      default: return (additionalFieldValues.value[field.name] ?? '').trim();
    }
  }

  function prefillFromCustomer(customer: GuestCustomerResponse) {
    guestCustomer.value = customer;
    firstname.value = customer.firstname;
    name.value = customer.name;
    email.value = customer.email;
    confirmEmail.value = customer.email;
    phone.value = customer.phone ?? '';
  }

  function prefillAdditionalFields() {
    for (const customerField of requiredCustomerFields.value) {
      if (CUSTOMER_FORM_SEMANTIC_TYPES.has(customerField.semanticType))
        continue;
      for (const itemField of customerField.itemFields) {
        const item = cartItems.value.find(cartItem => cartItem.itemId === itemField.itemId);
        const savedValue = item?.optionFieldValues.find(fieldValue => fieldValue.fieldName === itemField.sourceId);
        if (savedValue) {
          additionalFieldValues.value[customerField.name] = savedValue.value;
          break;
        }
      }
    }
  }

  watch(requiredCustomerFields, () => {
    prefillAdditionalFields();
  }, { immediate: true });

  async function loadExistingCustomer(): Promise<void> {
    isLoadingCustomer.value = true;
    try {
      const customer = await cartsTransactionsApi.fetchCustomer();
      prefillFromCustomer(customer);
    }
    catch {
      // Non-blocking: no customer cookie or customer not found — form stays empty
    }
    finally {
      isLoadingCustomer.value = false;
    }
  }

  function openModal() {
    submitError.value = null;
    isModalOpen.value = true;
  }

  function closeModal() {
    isModalOpen.value = false;
  }

  // Each requiredCustomerField was merged across cart items by the API (e.g. one "firstName" field
  // even if 3 items need it). itemFields maps back to each item's original field name (sourceId),
  // so we fan the single user-entered value out to every item that expects it.
  async function saveOptionFieldsForItems() {
    const fieldsByItem = new Map<string, Array<{ name: string; value: string }>>();

    for (const customerField of requiredCustomerFields.value) {
      const value = getValueForSemanticType(customerField);
      if (!value)
        continue;

      for (const itemField of customerField.itemFields) {
        if (!fieldsByItem.has(itemField.itemId)) {
          fieldsByItem.set(itemField.itemId, []);
        }
        fieldsByItem.get(itemField.itemId)!.push({
          name: itemField.sourceId,
          value,
        });
      }
    }

    const savePromises = Array.from(fieldsByItem.entries()).map(([itemId, fields]) => {
      return cartsTransactionsApi.saveParticipants(itemId, {
        participants: [],
        optionFields: fields,
      });
    });

    await Promise.all(savePromises);
  }

  async function submitGuestCustomer() {
    if (!isFormValid.value || isSubmitting.value)
      return;

    isSubmitting.value = true;
    submitError.value = null;

    try {
      const customerPayload = {
        firstname: firstname.value.trim(),
        name: name.value.trim(),
        email: email.value.trim(),
        phone: phone.value.trim() || undefined,
      };
      guestCustomer.value = guestCustomer.value
        ? await cartsTransactionsApi.updateCustomer(customerPayload)
        : await cartsTransactionsApi.createGuestCustomer(customerPayload);

      if (requiredCustomerFields.value.length > 0) {
        await saveOptionFieldsForItems();
      }

      closeModal();
    }
    catch (err) {
      console.error('[useGuestLogin] Error saving guest customer:', err);
      submitError.value = 'cart.guestLogin.submitError';
    }
    finally {
      isSubmitting.value = false;
    }
  }

  function hasExistingCustomer(): boolean {
    return guestCustomer.value !== null;
  }

  return {
    isModalOpen,
    isSubmitting,
    isLoadingCustomer,
    submitError,
    guestCustomer,

    firstname,
    name,
    email,
    confirmEmail,
    phone,
    additionalFieldValues,
    additionalFields,

    emailsMatch,
    emailFormatError,
    phoneFormatError,
    isFormValid,
    phoneRequired,

    openModal,
    closeModal,
    loadExistingCustomer,
    submitGuestCustomer,
    hasExistingCustomer,
  };
}
